import { Box, Circle, HStack, Icon, IconProps, Text } from "@chakra-ui/react";

/** Shared card look: white surface, soft border, rounded corners. */
export const cardProps = {
  bg: "white",
  borderWidth: "1px",
  borderColor: "gray.200",
  borderRadius: "xl",
  boxShadow: "sm",
} as const;

/** Small stroke icon helper (no extra icon package needed). */
export const Svg = ({ d, ...rest }: { d: string } & IconProps) => (
  <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <path d={d} />
  </Icon>
);

export const ICONS = {
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M8 13h8 M8 17h8",
  check: "M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4 12 14.01l-3-3",
  alert: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 8v4 M12 16h.01",
  clock: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2",
  calendar: "M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z M16 2v4 M8 2v4 M3 10h18",
};

type Tone = "blue" | "green" | "red" | "orange" | "gray";

/** Stat tile: tinted icon circle on the left, label + value on the right. */
export const StatCard = ({ label, value, icon, tone = "blue", help, valueColor, small }: { label: string; value: string; icon: string; tone?: Tone; help?: string; valueColor?: string; small?: boolean }) => (
  <HStack {...cardProps} p={4} spacing={3} align="center">
    <Circle size="40px" bg={`${tone}.50`} color={`${tone}.500`} flexShrink={0}>
      <Svg d={icon} boxSize={5} />
    </Circle>
    <Box minW={0}>
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
      <Text fontSize={small ? "md" : "xl"} fontWeight="semibold" color={valueColor ?? "gray.800"} noOfLines={1}>
        {value}
      </Text>
      {help && (
        <Text fontSize="xs" color="gray.400">
          {help}
        </Text>
      )}
    </Box>
  </HStack>
);

/** Small gray field label used above inputs. */
export const labelProps = { fontSize: "xs", color: "gray.500", fontWeight: "normal", mb: 1 } as const;

/** Table header cell look used across billing tables. */
export const thProps = { textTransform: "none", fontSize: "xs", fontWeight: "medium", color: "gray.500", letterSpacing: "normal", py: 3 } as const;
